import React, { useEffect, useState, useRef } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import WarningModal from './modal';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Html5Qrcode } from 'html5-qrcode';
import '../styling/containerStyles.css';
import { MaxCountResponse } from '../container/main';

interface QRBarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onScanSuccess: (scannedCode: string, couponData?: any, couponBrand?: string) => void;
  checkCouponCode: (code: string) => Promise<{ valid: boolean; data?: any; message?: string }>;
  scannedCoupons: Array<{ code: string; data: any; brand?: string }>;
  maxLimitCounts: MaxCountResponse;
  totalPtoR: number;
  ptoRFactor: number;
  brand: string;
  initialCouponCode?: string;
}

export const QRBarcodeScanner: React.FC<QRBarcodeScannerProps> = ({
  open,
  onClose,
  onScanSuccess,
  checkCouponCode,
  scannedCoupons,
  maxLimitCounts,
  totalPtoR,
  ptoRFactor,
  brand,
  initialCouponCode,
}) => {
  const [manualCode, setManualCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [scanMode, setScanMode] = useState<'manual' | 'camera'>('camera');
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerRef = useRef<HTMLDivElement>(null);
  const startAttemptsRef = useRef(0);
  const extractCouponCode = (scannedText: string): string => {
    const trimmed = scannedText.trim();
    
    if (trimmed.match(/^https?:\/\//i)) {
      try {
        const url = new URL(trimmed);
        const pathname = url.pathname;
        
        const match = pathname.match(/\/[Pp]\/([^\/\?]+)/);
        if (match && match[1]) {
          return match[1].toUpperCase();
        }
        
        const segments = pathname.split('/').filter(seg => seg.length > 0);
        if (segments.length > 0) {
          const lastSegment = segments[segments.length - 1];
          if (lastSegment.match(/^[A-Z0-9]{4,20}$/i)) {
            return lastSegment.toUpperCase();
          }
        }
        
        const codeParam = url.searchParams.get('code') || url.searchParams.get('coupon') || url.searchParams.get('c');
        if (codeParam) {
          return codeParam.toUpperCase();
        }
      } catch (e) {
        const match = trimmed.match(/\/[Pp]\/([A-Z0-9]+)/i);
        if (match && match[1]) {
          return match[1].toUpperCase();
        }
      }
    }
    
    return trimmed.toUpperCase();
  };

  const extractBrandFromScannedText = (scannedText: string, fallbackBrand: string): string => {
    const trimmed = scannedText.trim();

    if (trimmed.match(/^https?:\/\//i)) {
      try {
        const url = new URL(trimmed);
        const segments = url.pathname.split('/').filter(seg => seg.length > 0);
        if (segments.length >= 2) {
          const possibleBrand = segments[segments.length - 2];
          if (possibleBrand) {
            return possibleBrand.toLowerCase();
          }
        }
      } catch (e) {
      }
    }

    return fallbackBrand.toLowerCase();
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      startAttemptsRef.current = 0;
      setManualCode('');
      setError('');
      setShowErrorModal(false);
      setErrorMessage('');
      setScanMode('camera');
      setScanning(false);
      // Stop scanner if it's running
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current.clear();
        scannerRef.current = null;
      }
    } else {
      // Clean up scanner when dialog closes
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current.clear();
        scannerRef.current = null;
      }
    }
  }, [open]);

  // Handle scanner mode change
  useEffect(() => {
    if (open && scanMode === 'camera') {
      startScanner();
    } else if (scanMode === 'manual' && scannerRef.current) {
      stopScanner();
    }
  }, [scanMode, open]);

  const startScanner = async () => {
    try {
      // Avoid starting twice
      if (scannerRef.current) {
        return;
      }
      startAttemptsRef.current += 1;

      const html5QrCode = new Html5Qrcode("qr-reader");
      scannerRef.current = html5QrCode;

      setScanning(true);
      
      await html5QrCode.start(
        { facingMode: "environment" }, // Use back camera
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText: string) => {
          // Successfully scanned
          handleScannedCode(decodedText);
        },
        (errorMessage: string) => {
          // Scan error (ignore, will keep trying)
        }
      );
    } catch (err) {
      setScanning(false);
      if (startAttemptsRef.current < 2 && open && scanMode === 'camera') {
        setTimeout(() => {
          if (!scannerRef.current && open && scanMode === 'camera') {
            startScanner();
          }
        }, 500);
        return;
      }

      setErrorMessage('Failed to start camera. Please check camera permissions in your browser settings and try again.');
      setShowErrorModal(true);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
      } catch (err) {
        // Ignore stop errors
      }
      setScanning(false);
    }
  };

  const validateLimitsBeforeScan = (couponBrand: string): boolean => {
    if (!maxLimitCounts) return true;

    const { max, ...brands } = maxLimitCounts as any;
    const brandKeys = Object.keys(brands);

    // Helper: map any incoming brand string to one of the buckets
    // defined in maxLimitCounts (e.g. "woodgrip" or "other")
    const resolveBucketKey = (rawBrand: string | undefined): string => {
      const normalized = (rawBrand || '').trim().toLowerCase();
      if (!normalized) {
        return 'other';
      }

      // 1) Exact match
      let match = brandKeys.find(key => key.trim().toLowerCase() === normalized);
      if (match) {
        return match;
      }

      // 2) Substring match (e.g. "woodgrip-popular" -> "woodgrip")
      match = brandKeys.find(key => normalized.includes(key.trim().toLowerCase()));
      return match || 'other';
    };

    // Bucket for the coupon we are about to scan
    const couponBucketKey = resolveBucketKey(couponBrand || brand);
    const brandLimits = (brands as any)[couponBucketKey];

    // 1) Global max coupon count check
    if (typeof max === 'number' && scannedCoupons.length >= max) {

      setErrorMessage(`You can scan only ${max} coupon${max === 1 ? '' : 's'} at a time.`);
      setShowErrorModal(true);
      return false;
    }
    if (brandLimits) {
      const remainingAttempts = brandLimits.remaining_attempts;
      const remainingAmount = brandLimits.remaining_amount;

      // Count how many coupons already scanned that belong to the SAME BUCKET
      // (woodgrip vs other), regardless of their exact brand name.
      const usedForBrand = scannedCoupons.filter(sc => {
        const scBucketKey = resolveBucketKey(sc.brand || brand);
        return scBucketKey === couponBucketKey;
      }).length;

      // 2) Per-brand remaining attempts
      if (typeof remainingAttempts === 'number' && usedForBrand >= remainingAttempts) {
        const brandLabel = couponBucketKey === 'other' ? 'Popular' : couponBucketKey;
        setErrorMessage(`You have reached the maximum scan attempts for ${brandLabel}.`);
        setShowErrorModal(true);
        return false;
      }

      // 3) Per-brand remaining amount: compute amount only for this brand bucket
      if (typeof remainingAmount === 'number' && ptoRFactor > 0) {
        let usedAmountForBrand = 0;
        scannedCoupons.forEach(sc => {
          const scBucketKey = resolveBucketKey(sc.brand || brand);
          if (scBucketKey !== couponBucketKey) {
            return;
          }
          const codePoint = parseFloat(sc.data?.codePoint || '0');
          if (!codePoint || isNaN(codePoint)) {
            return;
          }
          usedAmountForBrand += codePoint * ptoRFactor;
        });

        if (usedAmountForBrand >= remainingAmount) {
          const brandLabel = couponBucketKey === 'other' ? 'Popular' : couponBucketKey;
          setErrorMessage(`You have reached the maximum redeemable amount for ${brandLabel}. Please try again tomorrow.`);
          setShowErrorModal(true);
          return false;
        }
      }
    }

    return true;
  };

  const handleScannedCode = async (code: string) => {
    const extractedCode = extractCouponCode(code);
    const couponBrand = extractBrandFromScannedText(code, brand);
    const normalized = extractedCode.toUpperCase();

    // Duplicate: already scanned or matches initial coupon code
    const isDuplicate =
      scannedCoupons.some(coupon => coupon.code === normalized) ||
      (initialCouponCode && normalized === initialCouponCode.toUpperCase());

    if (isDuplicate) {
      setErrorMessage('This coupon code has already been scanned.');
      setShowErrorModal(true);
      return;
    }

    if (!validateLimitsBeforeScan(couponBrand)) {
      return;
    }

    await stopScanner();

    setLoading(true);
    const result = await checkCouponCode(extractedCode);
    setLoading(false);

    if (result.valid && result.data) {
      onScanSuccess(extractedCode, result.data, couponBrand);
      setScanMode('manual');
    } else {
      setErrorMessage(result.message || 'Invalid coupon code');
      setShowErrorModal(true);
    }
  };

 
  const handleClose = async () => {
    // Stop scanner if running
    await stopScanner();
    setManualCode('');
    setError('');
    setShowErrorModal(false);
    setErrorMessage('');
    setScanMode('camera');
    onClose();
  };

  const handleCloseErrorModal = () => {
    setScanning(false);
    startScanner();
    setShowErrorModal(false);
    setErrorMessage('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Enter QR Code / Barcode</span>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {/* Camera Scanner Mode only */}
        {scanMode === 'camera' && (
          <div className="qr-reader-section">
            <div 
              id="qr-reader" 
              ref={scannerContainerRef}
              className="qr-reader-container"
            ></div>
            {loading && (
              <div className="qr-loading-overlay">
                <CircularProgress size={32} />
              </div>
            )}
            {scanning && !loading && (
              <div className="qr-scanning-text">
                <CircularProgress size={20} className="qr-scanning-spinner" />
                <span>Scanning... Point camera at QR code or barcode</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading || scanning}>
          Cancel
        </Button>
      </DialogActions>
      {showErrorModal && (
        <WarningModal 
          heading="Error" 
          content={errorMessage || 'The coupon code you entered is invalid. Please check and try again.'}
          open={showErrorModal}
          onClose={handleCloseErrorModal}
          showCloseButton={true}
        />
      )}
    </Dialog>
  );
};

export default QRBarcodeScanner;

